import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, Mail, Clock, Users, Layers, Sparkles, Globe, Database, Upload, FileText, Video, Link2 } from 'lucide-react';
import { EditableText, EditableImage, useAdmin } from './AdminContentEditor';
import SEO from './SEO';

const EmergentBIRPage = () => {
  const { isAdmin, isEditMode, sectionContent } = useAdmin();

  const tools = [
    { id: 'emergent', name: 'Emergent', description: 'Build platform' },
    { id: 'notion', name: 'Notion', description: 'Planning' },
    { id: 'cloudinary', name: 'Cloudinary', description: 'Media storage' },
    { id: 'gmail', name: 'Gmail', description: 'Auth flow' },
  ];

  const workshops1Hour = [
    {
      id: 'structuring',
      title: 'Structuring Before You Build',
      description: 'Define content, sections, and editable areas before prompting so builds are clean and scalable.',
      icon: Layers,
      color: 'emerald'
    },
    {
      id: 'userflow',
      title: 'User Flow Design',
      description: 'Map how users move through a product and translate that into working builds.',
      icon: Users,
      color: 'blue'
    },
    {
      id: 'editable',
      title: 'Editable Systems',
      description: 'Structure content so teams can update products without rebuilding.',
      icon: Sparkles,
      color: 'purple'
    }
  ];

  const workshops2Hour = [
    {
      id: 'product',
      title: 'Building a Product Layer',
      description: 'Extend a site into a product with accounts, saved content, and dynamic user experiences.',
      icon: Globe,
      color: 'orange'
    },
    {
      id: 'community',
      title: 'Community Systems',
      description: 'Create a system where users can upload, share, and interact with content.',
      icon: Users,
      color: 'pink'
    },
    {
      id: 'commerce',
      title: 'Content and Commerce',
      description: 'Build a system that supports product listings, digital content, and ongoing updates.',
      icon: Database,
      color: 'indigo'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600',
      blue: 'bg-blue-50 border-blue-200 text-blue-600',
      purple: 'bg-purple-50 border-purple-200 text-purple-600',
      orange: 'bg-orange-50 border-orange-200 text-orange-600',
      pink: 'bg-pink-50 border-pink-200 text-pink-600',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-600',
    };
    return colors[color] || colors.emerald;
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Emergent BIR - Workshop Sessions"
        description="Builder in Residence workshop sessions for Emergent"
        url="/EmergentBIR"
      />

      {/* Hero Section */}
      <section className="px-6 md:px-12 lg:px-24 pt-20 pb-16">
        <div className="max-w-5xl mx-auto">
          {/* Featured badge with 1 Hour label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 flex flex-wrap items-center gap-3"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 text-sm font-medium rounded-full border border-emerald-200">
              <Sparkles className="w-4 h-4" />
              Featured Session
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-full">
              <Clock className="w-4 h-4" />
              1 Hour Session
            </span>
          </motion.div>

          {/* Main Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-4"
          >
            <EditableText
              sectionId="bir-featured"
              field="title"
              defaultValue="Build Once, Edit Forever"
              as="span"
            />
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="text-xl md:text-2xl text-gray-500 font-medium mb-8"
          >
            <EditableText
              sectionId="bir-featured"
              field="subtitle"
              defaultValue="Inline Content Management for Emergent Websites"
              as="span"
            />
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-gray-600 leading-relaxed mb-10 max-w-3xl"
          >
            <EditableText
              sectionId="bir-featured"
              field="description"
              defaultValue="I will walk builders through how to turn a one time Emergent build into a living site that their team can update directly on the page. We will cover a simple admin login flow, in-line text editing and direct to page media uploads. I will also share reusable prompts so they can easily reference with their own products and communities."
              as="span"
            />
          </motion.p>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="flex flex-wrap gap-4"
          >
            <div className="flex flex-col gap-1">
              <a
                href={sectionContent?.['bir-featured']?.['workshop-url'] || '/InlineContentManagement'}
                onClick={(e) => { if (isEditMode) e.preventDefault(); }}
                className={`inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white font-medium rounded-2xl hover:bg-gray-800 transition-all hover:scale-105 shadow-lg shadow-gray-900/20 ${isEditMode ? 'cursor-default' : ''}`}
                data-testid="view-workshop-btn"
              >
                <EditableText sectionId="bir-featured" field="workshop-label" defaultValue="View Workshop Page" as="span" />
                <ArrowRight className="w-5 h-5" />
              </a>
              {isAdmin && isEditMode && (
                <EditableText sectionId="bir-featured" field="workshop-url" defaultValue="/InlineContentManagement" className="text-xs text-gray-400 italic" as="span" />
              )}
            </div>
            <div className="flex flex-col gap-1">
              <a
                href={sectionContent?.['bir-featured']?.['rsvp-url'] || '#'}
                onClick={(e) => { if (isEditMode) e.preventDefault(); }}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium rounded-2xl hover:from-emerald-600 hover:to-teal-600 transition-all hover:scale-105 shadow-lg shadow-emerald-500/20 ${isEditMode ? 'cursor-default' : ''}`}
                data-testid="rsvp-btn"
              >
                <EditableText sectionId="bir-featured" field="rsvp-label" defaultValue="RSVP" as="span" />
                <ExternalLink className="w-5 h-5" />
              </a>
              {isAdmin && isEditMode && (
                <EditableText sectionId="bir-featured" field="rsvp-url" defaultValue="https://your-rsvp-link.com" className="text-xs text-gray-400 italic" as="span" />
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* RSVP Context Section */}
      <section className="px-6 md:px-12 lg:px-24 py-12 bg-gradient-to-r from-gray-50 to-emerald-50/30">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8"
          >
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Text Content */}
              <div className="flex-1 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    <EditableText
                      sectionId="bir-rsvp-context"
                      field="title"
                      defaultValue="About the RSVP"
                      as="span"
                    />
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    <EditableText
                      sectionId="bir-rsvp-context"
                      field="description"
                      defaultValue="I have been building phygital in stealth and set up an RSVP system that helps me collect instructor insights to learn the audience before I speak to them. This allows me to tailor content to the specific needs and experience levels of each group, making the session more valuable for everyone."
                      as="span"
                    />
                  </p>
                </div>
              </div>

              {/* Image Placeholder */}
              <div className="flex-shrink-0 w-full md:w-64">
                <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-[4/3]">
                  <EditableImage
                    sectionId="bir-rsvp-context"
                    field="image"
                    defaultSrc=""
                    alt="RSVP system preview"
                    className="w-full h-full object-cover"
                    data-testid="rsvp-context-image"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tools Section with Uploadable Images */}
      <section className="px-6 md:px-12 lg:px-24 py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Tools Used</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {tools.map((tool, index) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl bg-white border border-gray-200 p-6 hover:border-gray-300 hover:shadow-lg transition-all"
              >
                {/* Uploadable Tool Logo */}
                <div className="w-16 h-16 rounded-xl bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center mb-4 overflow-hidden relative group-hover:border-gray-400 transition-colors">
                  <EditableImage
                    sectionId={`bir-tool-${tool.id}`}
                    field="logo"
                    defaultSrc=""
                    alt={`${tool.name} logo`}
                    className="w-full h-full object-contain p-2"
                  />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  <EditableText
                    sectionId={`bir-tool-${tool.id}`}
                    field="name"
                    defaultValue={tool.name}
                    as="span"
                  />
                </h3>
                <p className="text-sm text-gray-500">
                  <EditableText
                    sectionId={`bir-tool-${tool.id}`}
                    field="description"
                    defaultValue={tool.description}
                    as="span"
                  />
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Sessions */}
      <section className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-5xl mx-auto">
          {/* Section note */}
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-sm text-gray-400 mb-2"
          >
            <EditableText
              sectionId="bir-additional"
              field="note"
              defaultValue="Edit or replace sessions below based on audience and time"
              as="span"
            />
          </motion.p>

          {/* Section title */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-12"
          >
            <EditableText
              sectionId="bir-additional"
              field="title"
              defaultValue="Additional Sessions"
              as="span"
            />
          </motion.h2>

          {/* 1 Hour Workshops */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">1 Hour Workshops</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {workshops1Hour.map((workshop, index) => (
                <motion.div
                  key={workshop.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative overflow-hidden rounded-2xl border p-6 hover:shadow-lg transition-all ${getColorClasses(workshop.color)}`}
                >
                  <div className="mb-4">
                    <workshop.icon className="w-8 h-8" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    <EditableText
                      sectionId={`bir-workshop-${workshop.id}`}
                      field="title"
                      defaultValue={workshop.title}
                      as="span"
                    />
                  </h4>
                  <p className="text-gray-600 text-sm">
                    <EditableText
                      sectionId={`bir-workshop-${workshop.id}`}
                      field="description"
                      defaultValue={workshop.description}
                      as="span"
                    />
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* 2 Hour Workshops */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">2 Hour Workshops</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {workshops2Hour.map((workshop, index) => (
                <motion.div
                  key={workshop.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative overflow-hidden rounded-2xl border p-6 hover:shadow-lg transition-all ${getColorClasses(workshop.color)}`}
                >
                  <div className="mb-4">
                    <workshop.icon className="w-8 h-8" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    <EditableText
                      sectionId={`bir-workshop-${workshop.id}`}
                      field="title"
                      defaultValue={workshop.title}
                      as="span"
                    />
                  </h4>
                  <p className="text-gray-600 text-sm">
                    <EditableText
                      sectionId={`bir-workshop-${workshop.id}`}
                      field="description"
                      defaultValue={workshop.description}
                      as="span"
                    />
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Meet Your Guide Section */}
      <section className="px-6 md:px-12 lg:px-24 py-20">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-8 md:p-12"
          >
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
            </div>

            <div className="relative flex flex-col md:flex-row items-center gap-8">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl overflow-hidden ring-4 ring-white/20 shadow-2xl bg-gray-700">
                  <EditableImage
                    sectionId="bir-contact"
                    field="image"
                    defaultSrc=""
                    alt="Profile Photo"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 text-center md:text-left">
                <p className="text-emerald-400 text-sm font-medium uppercase tracking-wide mb-2">
                  <EditableText
                    sectionId="bir-contact"
                    field="section-title"
                    defaultValue="Meet Your Guide"
                    as="span"
                  />
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  <EditableText
                    sectionId="bir-contact"
                    field="name"
                    defaultValue="Naomi Thomas"
                    as="span"
                  />
                </h3>
                <p className="text-gray-400 mb-4 max-w-lg">
                  <EditableText
                    sectionId="bir-contact"
                    field="bio"
                    defaultValue="Building on Emergent since January 2026. Focused on turning one time builds into systems that teams can update and scale over time."
                    as="span"
                  />
                </p>
                <div className="flex items-center gap-4 justify-center md:justify-start">
                  <a 
                    href="mailto:me@iamnaomithomas.com" 
                    className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <EditableText
                      sectionId="bir-contact"
                      field="email"
                      defaultValue="me@iamnaomithomas.com"
                      as="span"
                    />
                  </a>
                  <a 
                    href="https://linkedin.com/in/techbaenae" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                    data-testid="bir-linkedin-link"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BIR Application Section */}
      <section className="px-6 md:px-12 lg:px-24 py-16 bg-gradient-to-b from-white to-emerald-50/30">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            {/* Emergent Logo */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-white border border-gray-200 shadow-lg overflow-hidden flex items-center justify-center">
              <EditableImage
                sectionId="bir-application"
                field="emergent-logo"
                defaultSrc=""
                alt="Emergent Logo"
                className="w-full h-full object-contain p-3"
              />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              <EditableText
                sectionId="bir-application"
                field="title"
                defaultValue="Emergent BIR Application Submission"
                as="span"
              />
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              <EditableText
                sectionId="bir-application"
                field="description"
                defaultValue="Resources and materials for the Builder in Residence application"
                as="span"
              />
            </p>
          </motion.div>

          {/* Application Links */}
          <div className="flex flex-col gap-4 max-w-3xl mx-auto">
            {/* Intro Video Card */}
            <motion.a
              href={(() => {
                const saved = sectionContent?.['bir-application']?.['video-url'];
                return saved || '#';
              })()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { if (isEditMode) e.preventDefault(); }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className={`group relative overflow-hidden rounded-2xl bg-white border border-gray-200 px-8 py-5 hover:border-emerald-300 hover:shadow-xl transition-all ${isEditMode ? 'cursor-default' : ''}`}
              data-testid="intro-video-card"
            >
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Video className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                    <EditableText sectionId="bir-application" field="video-title" defaultValue="Intro Video" as="span" />
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    <EditableText sectionId="bir-application" field="video-description" defaultValue="Watch my introduction and approach to building on Emergent" as="span" />
                  </p>
                </div>
                <span className="hidden sm:inline-flex items-center gap-1 text-sm text-emerald-600 font-medium whitespace-nowrap flex-shrink-0">
                  Open in Google Drive <ExternalLink className="w-4 h-4" />
                </span>
              </div>
              {isAdmin && isEditMode && (
                <div className="mt-2 pl-[68px]">
                  <EditableText sectionId="bir-application" field="video-url" defaultValue="https://drive.google.com/..." className="text-xs text-gray-400 italic" as="span" />
                </div>
              )}
            </motion.a>

            {/* Supporting Page Card */}
            <motion.a
              href={sectionContent?.['bir-application']?.['page-url'] || 'https://datadreaming.cloud/lab/frontrow/datadreaming'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { if (isEditMode) e.preventDefault(); }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className={`group relative overflow-hidden rounded-2xl bg-white border border-gray-200 px-8 py-5 hover:border-emerald-300 hover:shadow-xl transition-all ${isEditMode ? 'cursor-default' : ''}`}
              data-testid="supporting-website-card"
            >
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Link2 className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                    <EditableText sectionId="bir-application" field="page-title" defaultValue="Supporting Website" as="span" />
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    <EditableText sectionId="bir-application" field="page-description" defaultValue="View the Front Row lab showcasing the build journey and workshop content" as="span" />
                  </p>
                </div>
                <span className="hidden sm:inline-flex items-center gap-1 text-sm text-emerald-600 font-medium whitespace-nowrap flex-shrink-0">
                  Visit datadreaming.cloud <ExternalLink className="w-4 h-4" />
                </span>
              </div>
              {isAdmin && isEditMode && (
                <div className="mt-2 pl-[68px]">
                  <EditableText sectionId="bir-application" field="page-url" defaultValue="https://datadreaming.cloud/lab/frontrow/datadreaming" className="text-xs text-gray-400 italic" as="span" />
                </div>
              )}
            </motion.a>

            {/* Brain Food Resource Card */}
            <motion.a
              href={sectionContent?.['bir-application']?.['brainfood-url'] || '/resources/prompts/content-management'}
              onClick={(e) => { if (isEditMode) e.preventDefault(); }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className={`group relative overflow-hidden rounded-2xl bg-white border border-gray-200 px-8 py-5 hover:border-pink-300 hover:shadow-xl transition-all ${isEditMode ? 'cursor-default' : ''}`}
              data-testid="brain-food-card"
            >
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 group-hover:text-pink-600 transition-colors">
                    <EditableText sectionId="bir-application" field="brainfood-title" defaultValue="Brain Food: Content Management" as="span" />
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    <EditableText sectionId="bir-application" field="brainfood-description" defaultValue="Copy-paste prompts and step-by-step guide to build inline CMS on any Emergent project" as="span" />
                  </p>
                </div>
                <span className="hidden sm:inline-flex items-center gap-1 text-sm text-pink-600 font-medium whitespace-nowrap flex-shrink-0">
                  View Resource <ExternalLink className="w-4 h-4" />
                </span>
              </div>
              {isAdmin && isEditMode && (
                <div className="mt-2 pl-[68px]">
                  <EditableText sectionId="bir-application" field="brainfood-url" defaultValue="/resources/prompts/content-management" className="text-xs text-gray-400 italic" as="span" />
                </div>
              )}
            </motion.a>
          </div>
        </div>
      </section>

      {/* Bottom spacing */}
      <div className="h-12" />
    </div>
  );
};

export default EmergentBIRPage;
